import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, matchPath } from "react-router-dom";
import { trackPageview } from "../lib/analytics";
import PAGE_TITLES from "./pageTitles";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress, Box } from "@mui/material";

import {
  initAuth,
  selectCurrentUser,
  selectIsAuthenticated,
} from "../redux/slices/authSlice";
import { selectFeatureFlags, selectFeatureFlagsLoaded } from "../redux/slices/systemSettingsSlice";
import usePermissions from "../hooks/usePermissions";

import AdminLayout from "../layouts/AdminLayout";
import CustomerLayout from "../layouts/CustomerLayout";
import AuthLayout from "../layouts/AuthLayout";

import LoginPage from "../pages/auth/LoginPage";
import RegisterPage from "../pages/auth/RegisterPage";
import VerifyEmailPage from "../pages/auth/VerifyEmailPage";
import ForgotPasswordPage from "../pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "../pages/auth/ResetPasswordPage";

import AdminDashboard from "../pages/admin/AdminDashboard";
import AdminShipments from "../pages/admin/AdminShipments";
import AdminAddShipment from "../pages/admin/AdminAddShipment";
import AdminEditShipment from "../pages/admin/AdminEditShipment";
import AdminBulkAddShipments from "../pages/admin/AdminBulkAddShipments";
import AdminBulkImportShipments from "../pages/admin/AdminBulkImportShipments";
import AdminShipmentDetail from "../pages/admin/AdminShipmentDetail";
import AdminCustomers from "../pages/admin/AdminCustomers";
import AdminSailing from "../pages/admin/AdminSailing";
import AdminSailingDetail from "../pages/admin/AdminSailingDetail";
import AdminInvoices from "../pages/admin/AdminInvoices";
import AdminPayments from "../pages/admin/AdminPayments";
import AdminExpenses from "../pages/admin/AdminExpenses";
import AdminConsolidations from "../pages/admin/AdminConsolidations";
import AdminReports from "../pages/admin/AdminReports";
import AdminStats from "../pages/admin/AdminStats";
import AdminSettings from "../pages/admin/AdminSettings";
import AdminNotifications from "../pages/admin/AdminNotifications";
import AdminCustoms from "../pages/admin/AdminCustoms";
import AdminVehicles from "../pages/admin/AdminVehicles";

import CustomerDashboard from "../pages/customer/CustomerDashboard";
import CustomerShipments from "../pages/customer/CustomerShipments";
import CustomerBulkAddShipments from "../pages/customer/CustomerBulkAddShipments";
import CustomerBulkImportShipments from "../pages/customer/CustomerBulkImportShipments";
import CustomerAddShipment from "../pages/customer/CustomerAddShipment";
import CustomerEditShipment from "../pages/customer/CustomerEditShipment";
import CustomerConsolidations from "../pages/customer/CustomerConsolidations";
import CustomerUnclaimedPackages from "../pages/customer/CustomerUnclaimedPackages";
import CustomerShipmentDetail from "../pages/customer/CustomerShipmentDetail";
import CustomerUpload from "../pages/customer/CustomerUpload";
import CustomerNotifications from "../pages/customer/CustomerNotifications";
import CustomerPayments from "../pages/customer/CustomerPayments";
import CustomerInvoicePrint from "../pages/customer/CustomerInvoicePrint";
import CustomerProfile from "../pages/customer/CustomerProfile";
import CustomerRates from "../pages/customer/CustomerRates";
import CustomerPromos from "../pages/customer/CustomerPromos";
import CustomerReferrals from "../pages/customer/CustomerReferrals";
import CustomerUpgrade from "../pages/customer/CustomerUpgrade";
import ComingSoon from "../pages/customer/ComingSoon";
import LoadingScreen1 from "../components/common/LoadingScreen1";
import VehicleTrackingPage from "../pages/public/VehicleTrackingPage";

import AdminVehicleDetail from "../pages/admin/AdminVehicleDetail";
import AdminEmailTemplates from "../pages/admin/AdminEmailTemplates";
import AdminEmailLogs from "../pages/admin/AdminEmailLogs";
import AdminBolUpload from "../pages/admin/AdminBolUpload";
import AdminShopperRates from "../pages/admin/AdminShopperRates";
import AdminDeliveryReceipts from "../pages/admin/AdminDeliveryReceipts";
import AdminDeliveryReceiptPrint from "../pages/admin/AdminDeliveryReceiptPrint";
import AdminInvoicePrint from "../pages/admin/AdminInvoicePrint";
import AdminCodes from "../pages/admin/AdminCodes";
import AdminUnclaimedPackages from "../pages/admin/AdminUnclaimedPackages";
import AdminDockReceipts from "../pages/admin/AdminDockReceipts";
import AdminItemTracking from "../pages/admin/AdminItemTracking";
import AdminWarehouseScan from "../pages/admin/AdminWarehouseScan";
import AdminScan from "../pages/admin/AdminScan";
import AdminScanLogs from "../pages/admin/AdminScanLogs";
import AdminLiveTraffic from "../pages/admin/AdminLiveTraffic";
import AdminReferrals from "../pages/admin/AdminReferrals";
import AdminCustomerDetail from "../pages/admin/AdminCustomerDetail";
import AdminReadyToSail from "../pages/admin/AdminReadyToSail";
import AdminStatusSettings from "../pages/admin/AdminStatusSettings";
import AdminManifestPrint from "../pages/admin/AdminManifestPrint";
import AdminManifestFreightAllocation from "../pages/admin/AdminManifestFreightAllocation";
import AdminHouseBolPrint from "../pages/admin/AdminHouseBolPrint";
import AdminCustomsDocTemplates from "../pages/admin/AdminCustomsDocTemplates";
import AdminInvoiceRates from "../pages/admin/AdminInvoiceRates";
import AdminContainers from "../pages/admin/AdminContainers";
import AdminGenerateInvoice from "../pages/admin/AdminGenerateInvoice";
import AdminPromos from "../pages/admin/AdminPromos";

import RiderLayout from "../layouts/RiderLayout";
import RiderAvailableDeliveries from "../pages/rider/RiderAvailableDeliveries";
import RiderMyDeliveries from "../pages/rider/RiderMyDeliveries";
import RiderDeliveryConfirm from "../pages/rider/RiderDeliveryConfirm";
import AdminDeliveryReceiptTemplate from "../pages/admin/AdminDeliveryReceiptTemplate";
import AdminInvoiceTemplate from "../pages/admin/AdminInvoiceTemplate";
import AdminIntegrations from "../pages/admin/AdminIntegrations";
import AdminUpgradePage from "../pages/admin/AdminUpgradePage";



const getHomeRoute = (role) => {
  if (role === "admin") return "/admin";
  if (role === "rider") return "/rider";
  return "/dashboard";
};

const RequireAuth = ({ children, role, permission }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectCurrentUser);
  const { has } = usePermissions();

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (role && user?.role !== role) {
    return <Navigate to={getHomeRoute(user?.role)} replace />;
  }
  if (permission && !has(permission)) {
    return <Navigate to={getHomeRoute(user?.role)} replace />;
  }
  return children;
};

const RedirectIfAuthenticated = ({ children }) => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector(selectCurrentUser);
  if (isAuthenticated) {
    return <Navigate to={getHomeRoute(user?.role)} replace />;
  }
  return children;
};

// Gates an in-progress CUSTOMER-facing feature behind SystemSettings.
// featureFlags.<flag> (see CustomerLayout's fetchFeatureFlags dispatch) —
// renders ComingSoon while the flag is off, nothing while flags haven't
// loaded yet (avoids a flash of the wrong state), and the real page once
// it's confirmed on. Only used on customer routes; admin routes for the
// same features (AdminPromos, AdminReferrals) are never gated — admins
// need to keep building/testing regardless of what customers currently see.
const FeatureGate = ({ flag, title, description, children }) => {
  const featureFlags = useSelector(selectFeatureFlags);
  const featureFlagsLoaded = useSelector(selectFeatureFlagsLoaded);
  if (!featureFlagsLoaded) return null;
  if (!featureFlags[flag]) return <ComingSoon title={title} description={description} />;
  return children;
};

// Sends a GA4 pageview on every client-side route change — has to live
// inside <BrowserRouter> (useLocation needs Router context), which is why
// this can't just be a plain effect in AppRouter itself, that component
// renders BrowserRouter rather than being inside it.
const RouteTracker = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageview(location.pathname + location.search);
  }, [location.pathname, location.search]);

  // Sets the browser tab title per page, so pages are distinguishable when
  // several are open at once — falls back to the bare app name for any
  // route not listed in pageTitles.js (e.g. a new page someone forgot to add).
  useEffect(() => {
    const match = PAGE_TITLES.find((r) => matchPath({ path: r.path, end: true }, location.pathname));
    document.title = match ? `${match.title} | Waypoint Imports` : "Waypoint Imports";
  }, [location.pathname]);

  return null;
};

const AppRouter = () => {
  const [showLoader, setShowLoader] = useState(true);
  const dispatch = useDispatch();
  const { initialized } = useSelector((s) => s.auth);

  // ✅ ONLY ONCE: clean bootstrap
  useEffect(() => {
    dispatch(initAuth());
  }, [dispatch]);

  // Kept comfortably ABOVE LoadingScreen1's own animation duration (5000ms
  // total — 3s icon draw-in + 2s "Waypoint Imports" wordmark reveal, see
  // LoadingScreen1.jsx) so the loader never unmounts mid-animation, cutting
  // the wordmark off before it's shown.
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowLoader(false);
    }, 5600);

    return () => clearTimeout(timer);
  }, []);

  if (!initialized || showLoader) {
    return <LoadingScreen1 sx={{ width: 300 }} />;
  }

  return (
    <BrowserRouter>
      <RouteTracker />
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />

        {/* Public — no auth required */}
        <Route path="/track/:trackingId?" element={<VehicleTrackingPage />} />

        <Route element={<AuthLayout />}>
          <Route
            path="/login"
            element={
              <RedirectIfAuthenticated>
                <LoginPage />
              </RedirectIfAuthenticated>
            }
          />
          <Route
            path="/register"
            element={
              <RedirectIfAuthenticated>
                <RegisterPage />
              </RedirectIfAuthenticated>
            }
          />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>

        <Route
          path="/admin"
          element={
            <RequireAuth role="admin">
              <AdminLayout />
            </RequireAuth>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="shipments" element={<RequireAuth permission="view_shipments"><AdminShipments /></RequireAuth>} />
          <Route path="shipments/add" element={<RequireAuth permission="view_shipments"><AdminAddShipment /></RequireAuth>} />
          <Route path="shipments/bulk-add" element={<RequireAuth permission="view_shipments"><AdminBulkAddShipments /></RequireAuth>} />
          <Route path="shipments/bulk-import" element={<RequireAuth permission="view_shipments"><AdminBulkImportShipments /></RequireAuth>} />
          <Route path="shipments/:id/edit" element={<RequireAuth permission="view_shipments"><AdminEditShipment /></RequireAuth>} />
          <Route path="shipments/:id" element={<RequireAuth permission="view_shipments"><AdminShipmentDetail /></RequireAuth>} />
          <Route path="customers" element={<RequireAuth permission="view_users"><AdminCustomers /></RequireAuth>} />
          <Route path="customers/:id" element={<RequireAuth permission="view_users"><AdminCustomerDetail /></RequireAuth>} />
          <Route path="customs" element={<RequireAuth permission="view_customs"><AdminCustoms /></RequireAuth>} />
          <Route path="sailing/:id/manifest" element={<RequireAuth permission="view_sailing"><AdminManifestPrint /></RequireAuth>} />
          <Route path="sailing/:id/freight-allocation" element={<RequireAuth permission="view_sailing"><AdminManifestFreightAllocation /></RequireAuth>} />
          <Route path="sailing/:id/house-bol/:customerId" element={<RequireAuth permission="view_sailing"><AdminHouseBolPrint /></RequireAuth>} />
          <Route path="customs-doc-templates" element={<RequireAuth permission="manage_settings"><AdminCustomsDocTemplates /></RequireAuth>} />
          <Route path="invoice-rates" element={<RequireAuth permission="manage_settings"><AdminInvoiceRates /></RequireAuth>} />
          <Route path="containers" element={<RequireAuth permission="view_sailing"><AdminContainers /></RequireAuth>} />
          <Route path="vehicles" element={<RequireAuth permission="view_vehicles"><AdminVehicles /></RequireAuth>} />
          <Route path="vehicles/:id" element={<RequireAuth permission="view_vehicles"><AdminVehicleDetail /></RequireAuth>} />
          <Route path="bol-upload" element={<RequireAuth permission="upload_bol"><AdminBolUpload /></RequireAuth>} />
          <Route path="sailing" element={<RequireAuth permission="view_sailing"><AdminSailing /></RequireAuth>} />
          <Route path="sailing/:id" element={<RequireAuth permission="view_sailing"><AdminSailingDetail /></RequireAuth>} />
          <Route path="delivery-receipts" element={<RequireAuth permission="view_delivery_receipts"><AdminDeliveryReceipts /></RequireAuth>} />
          <Route path="delivery-receipts/:id/print" element={<RequireAuth permission="view_delivery_receipts"><AdminDeliveryReceiptPrint /></RequireAuth>} />
          <Route path="invoices" element={<RequireAuth permission="view_invoices"><AdminInvoices /></RequireAuth>} />
          <Route path="payments" element={<RequireAuth permission="view_payments"><AdminPayments /></RequireAuth>} />
          <Route path="expenses" element={<RequireAuth permission="view_sailing"><AdminExpenses /></RequireAuth>} />
          <Route path="generate-invoice" element={<RequireAuth permission="manage_invoices"><AdminGenerateInvoice /></RequireAuth>} />
          <Route path="promos" element={<RequireAuth permission="manage_invoices"><AdminPromos /></RequireAuth>} />
          <Route path="invoices/:id/print" element={<RequireAuth permission="view_invoices"><AdminInvoicePrint /></RequireAuth>} />
          <Route path="consolidations" element={<RequireAuth permission="view_consolidations"><AdminConsolidations /></RequireAuth>} />
          <Route path="invoice-template" element={<RequireAuth permission="manage_settings"><AdminInvoiceTemplate /></RequireAuth>} />
          <Route path="integrations" element={<RequireAuth permission="manage_settings"><AdminIntegrations /></RequireAuth>} />
          <Route path="upgrade-page" element={<RequireAuth permission="manage_settings"><AdminUpgradePage /></RequireAuth>} />
          <Route path="reports" element={<RequireAuth permission="view_reports"><AdminReports /></RequireAuth>} />
          <Route path="stats" element={<RequireAuth permission="view_reports"><AdminStats /></RequireAuth>} />
          <Route path="email-templates" element={<RequireAuth permission="view_email_templates"><AdminEmailTemplates /></RequireAuth>} />
          <Route path="email-logs" element={<RequireAuth permission="view_email_logs"><AdminEmailLogs /></RequireAuth>} />
          <Route path="settings" element={<RequireAuth permission="manage_settings"><AdminSettings /></RequireAuth>} />
          <Route path="notifications" element={<RequireAuth permission="view_notifications"><AdminNotifications /></RequireAuth>} />
          <Route path="shopper-rates" element={<RequireAuth permission="manage_shopper_rates"><AdminShopperRates /></RequireAuth>} />
          <Route path="codes" element={<RequireAuth permission="manage_customs"><AdminCodes /></RequireAuth>} />
          <Route path="delivery-receipt-template" element={<RequireAuth permission="manage_settings"><AdminDeliveryReceiptTemplate /></RequireAuth>} />
          <Route path="unclaimed-packages" element={<RequireAuth permission="view_unclaimed_packages"><AdminUnclaimedPackages /></RequireAuth>} />
          <Route path="dock-receipts" element={<RequireAuth permission="manage_shipments"><AdminDockReceipts /></RequireAuth>} />
          <Route path="scan" element={<RequireAuth permission="manage_shipments"><AdminWarehouseScan /></RequireAuth>} />
          <Route path="scan/log" element={<RequireAuth permission="manage_shipments"><AdminScanLogs /></RequireAuth>} />
          <Route path="scan/:mode" element={<RequireAuth permission="manage_shipments"><AdminScan /></RequireAuth>} />
          <Route path="item-tracking" element={<RequireAuth permission="manage_shipments"><AdminItemTracking /></RequireAuth>} />
          <Route path="ready-to-sail" element={<RequireAuth permission="manage_shipments"><AdminReadyToSail /></RequireAuth>} />
          <Route path="status-settings" element={<RequireAuth permission="view_status_settings"><AdminStatusSettings /></RequireAuth>} />
          <Route path="live-traffic" element={<RequireAuth permission="view_users"><AdminLiveTraffic /></RequireAuth>} />
          <Route path="referrals" element={<RequireAuth permission="view_users"><AdminReferrals /></RequireAuth>} />
        </Route>

        <Route
          path="/dashboard"
          element={
            <RequireAuth role="customer">
              <CustomerLayout />
            </RequireAuth>
          }
        >
          <Route index element={<CustomerDashboard />} />
          <Route path="shipments" element={<CustomerShipments />} />
          <Route path="shipment/add" element={<CustomerAddShipment />} />
          <Route path="shipment/:id/edit" element={<CustomerEditShipment />} />
          <Route path="shipments/bulk-add" element={<CustomerBulkAddShipments />} />
          <Route path="shipments/bulk-import" element={<CustomerBulkImportShipments />} />
          <Route path="shipments/:id" element={<CustomerShipmentDetail />} />
          <Route path="consolidations" element={<CustomerConsolidations />} />
          <Route path="unclaimed-packages" element={<CustomerUnclaimedPackages />} />
          <Route path="upload/:shipmentId?" element={<CustomerUpload />} />
          <Route path="rates" element={<CustomerRates />} />
          <Route
            path="promos"
            element={
              <FeatureGate flag="promosEnabled" title="Promos" description="Promo codes are on the way — check back soon for savings on your shipments.">
                <CustomerPromos />
              </FeatureGate>
            }
          />
          <Route
            path="referrals"
            element={
              <FeatureGate flag="referralsEnabled" title="Referrals" description="Our referral program is almost ready — you'll soon be able to earn rewards for sharing Waypoint with friends.">
                <CustomerReferrals />
              </FeatureGate>
            }
          />
          <Route path="upgrade" element={<CustomerUpgrade />} />
          <Route path="payments" element={<CustomerPayments />} />
          <Route path="invoices/:id/print" element={<CustomerInvoicePrint />} />
          <Route path="notifications" element={<CustomerNotifications />} />
          <Route path="profile" element={<CustomerProfile />} />
        </Route>

        <Route
          path="/rider"
          element={
            <RequireAuth role="rider">
              <RiderLayout />
            </RequireAuth>
          }
        >
          <Route index element={<RiderAvailableDeliveries />} />
          <Route path="mine" element={<RiderMyDeliveries />} />
          <Route path="deliver/:id" element={<RiderDeliveryConfirm />} />
        </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default AppRouter;
